import React, { useState, useEffect, useCallback, useRef } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { CalendarIcon, MapPinIcon, TagIcon, BackIcon, CheckIcon } from '../components/common/Icons';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import usePermissions from '../hooks/usePermissions';

const STATUS_LABELS = { planned: 'Planifiée', ongoing: 'En cours', completed: 'Terminée', cancelled: 'Annulée' };
const STATUS_COLORS = {
  planned: { bg: '#eff6ff', color: '#1e40af' },
  ongoing: { bg: '#d1fae5', color: '#065f46' },
  completed: { bg: '#f0fdf4', color: '#166534' },
  cancelled: { bg: '#fef2f2', color: '#b91c1c' },
};

const styles = {
  page: { width: '100%' },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    color: '#1e40af', fontSize: '0.9rem', fontWeight: 600,
    cursor: 'pointer', background: 'none', border: 'none',
    padding: '0', marginBottom: '16px',
  },
  card: {
    backgroundColor: 'white', borderRadius: '12px',
    padding: '24px', border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: '20px',
  },
  title: { fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' },
  meta: { fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' },
  badge: { fontSize: '0.75rem', padding: '3px 10px', borderRadius: '999px', fontWeight: 600, display: 'inline-block', marginLeft: '8px' },
  tabs: { display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' },
  tab: (active) => ({
    padding: '10px 24px', cursor: 'pointer', fontWeight: 600,
    fontSize: '0.92rem', border: 'none', background: 'none',
    color: active ? '#1e40af' : '#64748b',
    borderBottom: active ? '2px solid #1e40af' : '2px solid transparent',
    marginBottom: '-2px', transition: 'color 0.15s',
  }),
  countGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '12px', marginBottom: '20px',
  },
  countCell: {
    backgroundColor: '#f8fafc', borderRadius: '10px',
    padding: '12px', border: '1px solid #e2e8f0', textAlign: 'center',
  },
  countLabel: { fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '8px' },
  countValue: { fontSize: '1.6rem', fontWeight: 800, color: '#1e40af', marginBottom: '4px' },
  countBtns: { display: 'flex', gap: '6px', justifyContent: 'center' },
  countBtn: (variant) => ({
    width: '32px', height: '32px', borderRadius: '6px',
    border: 'none', cursor: 'pointer', fontSize: '1.1rem', fontWeight: 700,
    backgroundColor: variant === 'plus' ? '#1e40af' : '#f1f5f9',
    color: variant === 'plus' ? 'white' : '#374151',
  }),
  btnPrimary: {
    backgroundColor: '#1e40af', color: 'white', border: 'none',
    borderRadius: '8px', padding: '10px 24px', cursor: 'pointer',
    fontSize: '0.95rem', fontWeight: 700,
  },
  searchInput: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '0.95rem', marginBottom: '12px',
  },
  participantRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', backgroundColor: '#f8fafc',
    borderRadius: '8px', marginBottom: '6px',
    border: '1px solid #e2e8f0',
  },
  attendeeRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', backgroundColor: '#f0fdf4',
    borderRadius: '8px', marginBottom: '6px',
    border: '1px solid #bbf7d0',
  },
  totalSummary: {
    backgroundColor: '#1e40af', color: 'white', borderRadius: '10px',
    padding: '16px 20px', marginTop: '16px',
    display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center',
  },
};

const COUNT_FIELDS = [
  { key: 'hommes', label: 'Hommes' },
  { key: 'femmes', label: 'Femmes' },
  { key: 'enfants', label: 'Enfants' },
  { key: 'nouveaux', label: 'Nouveaux' },
  { key: 'membres', label: 'Membres' },
  { key: 'serviteurs', label: 'Serviteurs' },
  { key: 'invites', label: 'Invités' },
];

export default function ActivityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState(null);
  const [bulkCount, setBulkCount] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('bulk');
  const [registrations, setRegistrations] = useState([]);
  const [counts, setCounts] = useState({ hommes: 0, femmes: 0, enfants: 0, nouveaux: 0, membres: 0, serviteurs: 0, invites: 0 });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [attendeeFilter, setAttendeeFilter] = useState(''); // filtre dans la liste des présents
  const [regFilter, setRegFilter] = useState(''); // filtre dans les inscrits
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // record id à supprimer

  const { hasGroupAccess } = usePermissions();
  const canEdit = user?.role === 'admin' || user?.role === 'superviseur' || user?.role === 'responsable' || user?.role === 'accueil' || user?.role === 'superadmin_platform' || hasGroupAccess('activities') || hasGroupAccess('attendance');
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  // Calcul présents/absents parmi les inscrits (utilisé dans le rendu + export CSV)
  const presentIds = new Set(attendees.filter(a => !a.is_anonymous && !a.is_registration).map(a => a.participant));
  const regPresents = registrations.filter(r => presentIds.has(r.participant_id));
  const regAbsents  = registrations.filter(r => !presentIds.has(r.participant_id));

  const downloadAbsentsCSV = () => {
    if (regAbsents.length === 0) return;
    const STATUS_FR = { nouveau: 'Nouveau', membre: 'Membre', serviteur: 'Serviteur', invite: 'Invité' };
    const rows = [
      ['Code membre', 'Nom complet', 'Statut', 'Église', 'Famille', 'Inscrit le'],
      ...regAbsents.map(r => [
        r.member_code || '',
        r.full_name || '',
        STATUS_FR[r.status] || r.status || '',
        r.church_name || 'Non classé',
        r.family_name || '',
        r.registered_at ? new Date(r.registered_at).toLocaleDateString('fr-FR') : '',
      ]),
    ];
    const bom = '﻿';
    const csv = bom + rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `absents-${activity?.name?.replace(/\s+/g, '-')}-${activity?.date}.csv`;
    a.click();
  };

  const printAttendanceSheet = () => {
    const presenceList = attendees.filter(a => !a.is_registration);
    const actName = activity?.name || 'Activité';
    const actDate = activity?.date || '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Feuille de présence — ${actName}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
      h1 { font-size: 18px; margin-bottom: 4px; }
      .meta { font-size: 13px; color: #555; margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #1e40af; color: white; padding: 8px 12px; text-align: left; font-size: 12px; }
      td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
      tr:nth-child(even) td { background: #f8fafc; }
      .sig { width: 100px; }
      @media print { body { padding: 0; } }
    </style></head><body>
    <h1>Feuille de présence — ${actName}</h1>
    <div class="meta">Date : ${actDate}${activity?.start_time ? ' · ' + activity.start_time.slice(0,5) : ''}${activity?.location ? ' · ' + activity.location : ''} · Total : ${presenceList.length} présent(s)</div>
    <table><thead><tr><th>#</th><th>Nom complet</th><th>Code membre</th><th>Statut</th><th>Famille</th><th class="sig">Signature</th></tr></thead><tbody>
    ${presenceList.map((a, i) => `<tr><td>${i+1}</td><td>${a.participant_detail?.full_name || (a.is_anonymous ? `Anonyme (${a.anonymous_gender || ''})` : '—')}</td><td>${a.participant_detail?.member_code || '—'}</td><td>${a.participant_detail?.status || '—'}</td><td>${a.participant_detail?.family_name || '—'}</td><td></td></tr>`).join('')}
    </tbody></table></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const exportCSV = () => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const url = `${base}/attendance/export/?activity=${id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `presences-${activity?.name?.replace(/\s+/g,'-')}-${activity?.date}.csv`;
        a.click();
      });
  };

  const downloadQR = async () => {
    if (!qrRef.current) return;
    const canvas = await html2canvas(qrRef.current, { scale: 4, backgroundColor: '#ffffff' });
    const a = document.createElement('a');
    a.download = `qr-${activity?.name?.replace(/\s+/g, '-')}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  };


  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const [actRes, attendRes, regRes] = await Promise.all([
        api.get(`/activities/${id}/`),
        api.get(`/attendance/records/?activity=${id}&page_size=500`),
        api.get(`/attendance/registrations/?activity=${id}&page_size=500`),
      ]);
      setActivity(actRes.data);
      const recs = Array.isArray(attendRes.data) ? attendRes.data : attendRes.data.results || [];
      setAttendees(recs);
      setRegistrations(Array.isArray(regRes.data) ? regRes.data : []);

      const bulkRes = await api.get(`/attendance/bulk/?activity=${id}`);
      const bulkData = Array.isArray(bulkRes.data) ? bulkRes.data : bulkRes.data.results || [];
      if (bulkData.length > 0) {
        setBulkCount(bulkData[0]);
        const b = bulkData[0];
        setCounts({ hommes: b.hommes, femmes: b.femmes, enfants: b.enfants, nouveaux: b.nouveaux, membres: b.membres, serviteurs: b.serviteurs, invites: b.invites });
      }
    } catch {
      toast.error('Erreur lors du chargement de l\'activité.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const handleCount = (key, delta) => {
    setCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
  };

  const saveBulkCount = async () => {
    setSaving(true);
    try {
      const payload = { activity: parseInt(id), ...counts };
      await api.post('/attendance/bulk/', payload);
      toast.success('Comptage enregistré !');
      fetchActivity();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const searchParticipants = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await api.get(`/participants/?search=${encodeURIComponent(q)}`);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      // Filter out already-present participants
      const presentIds = attendees.filter((a) => a.participant).map((a) => a.participant);
      setSearchResults(data.filter((p) => !presentIds.includes(p.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [attendees]);

  useEffect(() => {
    const timer = setTimeout(() => searchParticipants(search), 350);
    return () => clearTimeout(timer);
  }, [search, searchParticipants]);

  const markPresent = async (participant) => {
    try {
      await api.post('/attendance/records/', {
        activity: parseInt(id),
        participant: participant.id,
        is_anonymous: false,
      });
      toast.success(`${participant.full_name} marqué(e) présent(e) !`);
      setSearch('');
      setSearchResults([]);
      fetchActivity();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('Ce participant est déjà enregistré.');
      } else {
        toast.error('Erreur lors de l\'enregistrement.');
      }
    }
  };

  const notifyAbsents = async () => {
    setNotifyLoading(true);
    try {
      const res = await api.post(`/attendance/notify-absents/`, { activity: parseInt(id) });
      setNotifyResult(res.data);
    } catch {
      toast.error('Erreur lors de l\'envoi des notifications.');
    } finally {
      setNotifyLoading(false);
    }
  };

  const removeAttendee = async (recordId) => {
    try {
      await api.delete(`/attendance/records/${recordId}/`);
      toast.success('Présence supprimée.');
      setConfirmRemove(null);
      fetchActivity();
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <LoadingSpinner size={40} label="Chargement de l'activité..." />
      </div>
    );
  }

  if (!activity) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Activité introuvable.</div>;

  const sc = STATUS_COLORS[activity.status] || STATUS_COLORS.planned;
  const total = counts.hommes + counts.femmes + counts.enfants;

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={() => navigate('/activites')}>
        <BackIcon size={15} color="currentColor" style={{ marginRight: 4 }} /> Retour aux activités
      </button>

      {/* Header card */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={styles.title}>
              {activity.name}
              <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.color }}>
                {STATUS_LABELS[activity.status]}
              </span>
            </h1>
            <p style={styles.meta}><CalendarIcon size={14} color="#64748b" /> {activity.date} · {activity.start_time?.slice(0, 5)}{activity.end_time ? ` - ${activity.end_time.slice(0, 5)}` : ''}</p>
            <p style={styles.meta}><MapPinIcon size={14} color="#64748b" /> {activity.location}</p>
            {activity.activity_type_detail && (
              <p style={styles.meta}><TagIcon size={14} color="#64748b" /> {activity.activity_type_detail.name}</p>
            )}
            {activity.description && <p style={{ ...styles.meta, marginTop: '8px', fontStyle: 'italic' }}>{activity.description}</p>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#1e40af' }}>{activity.total_attendance}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>présences totales</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {/* Bouton impression */}
              {canEdit && (
                <button onClick={printAttendanceSheet}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px', backgroundColor: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: '#c2410c' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Imprimer
                </button>
              )}
              {/* Bouton export CSV */}
              {canEdit && (
                <button onClick={exportCSV}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px', backgroundColor: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: '#16a34a' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Export CSV
                </button>
              )}
              {/* Bouton QR activité */}
              {activity.qr_code && (
                <button onClick={() => setShowQR(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px', backgroundColor: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: '#1e40af' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zm3 3h4v4h-4zm-3 3h3"/></svg>
                  QR Présence
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={!!confirmRemove}
        title="Supprimer cette présence ?"
        message="La présence de ce participant sera retirée de l'activité."
        confirmLabel="Supprimer"
        onConfirm={() => removeAttendee(confirmRemove)}
        onCancel={() => setConfirmRemove(null)}
      />

      {/* Modal résultat notification absents */}
      {notifyResult && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={() => setNotifyResult(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>Résultat des notifications</div>
              <button onClick={() => setNotifyResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {notifyResult.absent_count === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#16a34a' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 10px' }}><polyline points="20 6 9 17 4 12"/></svg>
                <div style={{ fontWeight: 700 }}>Tous les inscrits étaient présents !</div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, backgroundColor: '#fff7ed', borderRadius: '10px', padding: '12px 16px', border: '1px solid #fed7aa', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#c2410c' }}>{notifyResult.absent_count}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9a3412', fontWeight: 600 }}>absent(s)</div>
                  </div>
                  <div style={{ flex: 1, backgroundColor: notifyResult.email_enabled ? '#f0fdf4' : '#f8fafc', borderRadius: '10px', padding: '12px 16px', border: `1px solid ${notifyResult.email_enabled ? '#bbf7d0' : '#e2e8f0'}`, textAlign: 'center' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: notifyResult.email_enabled ? '#16a34a' : '#94a3b8' }}>{notifyResult.emails_sent}</div>
                    <div style={{ fontSize: '0.75rem', color: notifyResult.email_enabled ? '#15803d' : '#94a3b8', fontWeight: 600 }}>email(s) envoyé(s)</div>
                  </div>
                </div>
                {!notifyResult.email_enabled && (
                  <p style={{ fontSize: '0.78rem', color: '#f59e0b', backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px' }}>
                    Email désactivé — configurez le SMTP dans Paramètres pour envoyer les emails.
                  </p>
                )}
                <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                  {notifyResult.absents.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, color: '#b91c1c', flexShrink: 0 }}>
                        {p.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>{p.full_name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.member_code}{p.email ? ` · ${p.email}` : ' · pas d\'email'}</div>
                      </div>
                      {p.email ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            <button onClick={() => setNotifyResult(null)}
              style={{ width: '100%', marginTop: '16px', padding: '10px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' }}>
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Modal QR code activité */}
      {showQR && activity.qr_code && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={() => setShowQR(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '32px', maxWidth: '380px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            {/* En-tête modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>QR Code — Présence</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>À afficher ou imprimer à l'église</div>
              </div>
              <button onClick={() => setShowQR(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* QR imprimable */}
            <div ref={qrRef} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1.5px solid #e2e8f0', textAlign: 'center' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>
                Scannez pour marquer votre présence
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b', marginBottom: '4px' }}>{activity.name}</div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '16px' }}>
                {activity.date} · {activity.start_time?.slice(0, 5)} · {activity.location}
              </div>
              <div style={{ display: 'inline-block', padding: '12px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9' }}>
                <QRCodeSVG value={`${window.location.origin}/scan?activity=${activity.qr_code}`} size={180} fgColor="#1e293b" level="M" />
              </div>
              <div style={{ marginTop: '12px', fontFamily: 'monospace', fontSize: '0.62rem', color: '#cbd5e1', letterSpacing: '0.05em' }}>
                {activity.qr_code}
              </div>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={downloadQR}
                style={{ flex: 1, padding: '10px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Télécharger
              </button>
              <button onClick={() => window.print()}
                style={{ flex: 1, padding: '10px', backgroundColor: '#f8fafc', color: '#374151', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                Imprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {canEdit && (
        <>
          <div style={styles.tabs}>
            <button style={styles.tab(tab === 'bulk')} onClick={() => setTab('bulk')}>Comptage rapide</button>
            <button style={styles.tab(tab === 'individual')} onClick={() => setTab('individual')}>Présences individuelles</button>
            <button style={styles.tab(tab === 'registrations')} onClick={() => setTab('registrations')}>
              Inscrits
              {registrations.length > 0 && (
                <span style={{ marginLeft: '6px', fontSize: '0.7rem', fontWeight: 800, backgroundColor: '#1e40af', color: 'white', borderRadius: '999px', padding: '1px 7px' }}>
                  {registrations.length}
                </span>
              )}
            </button>
          </div>

          {tab === 'bulk' && (
            <div style={styles.card}>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '16px' }}>
                Enregistrer le comptage
              </div>
              <div style={styles.countGrid}>
                {COUNT_FIELDS.map((field) => (
                  <div key={field.key} style={styles.countCell}>
                    <div style={styles.countLabel}>{field.label}</div>
                    <div style={styles.countValue}>{counts[field.key]}</div>
                    <div style={styles.countBtns}>
                      <button style={styles.countBtn('minus')} onClick={() => handleCount(field.key, -1)}>−</button>
                      <button style={styles.countBtn('plus')} onClick={() => handleCount(field.key, 1)}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.totalSummary}>
                <div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Total présences (Hommes + Femmes + Enfants)</div>
                  <div style={{ fontSize: '2rem', fontWeight: 800 }}>{total}</div>
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {[
                      { key: 'hommes', label: 'H' }, { key: 'femmes', label: 'F' }, { key: 'enfants', label: 'E' },
                    ].filter(f => counts[f.key] > 0).map(f => (
                      <span key={f.key}>{counts[f.key]}{f.label}</span>
                    ))}
                  </div>
                  {[
                    { key: 'nouveaux', label: 'Nv' }, { key: 'membres', label: 'Mb' },
                    { key: 'serviteurs', label: 'Sv' }, { key: 'invites', label: 'Inv' },
                  ].some(f => counts[f.key] > 0) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '0.75rem', opacity: 0.75 }}>
                      <span style={{ fontStyle: 'italic' }}>déjà inclus ci-dessus :</span>
                      {[
                        { key: 'nouveaux', label: 'Nv' }, { key: 'membres', label: 'Mb' },
                        { key: 'serviteurs', label: 'Sv' }, { key: 'invites', label: 'Inv' },
                      ].filter(f => counts[f.key] > 0).map(f => (
                        <span key={f.key}>{counts[f.key]}{f.label}</span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  style={{ ...styles.btnPrimary, backgroundColor: 'white', color: '#1e40af', marginLeft: 'auto' }}
                  onClick={saveBulkCount}
                  disabled={saving}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer le comptage'}
                </button>
              </div>
            </div>
          )}

          {tab === 'registrations' && (
            <div style={styles.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Participants inscrits</span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', backgroundColor: '#f1f5f9', borderRadius: '999px', padding: '2px 10px' }}>
                    {registrations.length} inscrits
                  </span>
                </div>
                {registrations.length > 0 && (
                  <button
                    onClick={notifyAbsents}
                    disabled={notifyLoading}
                    style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px', backgroundColor: notifyLoading ? '#f1f5f9' : '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '8px', cursor: notifyLoading ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 700, color: '#c2410c' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    {notifyLoading ? 'Envoi...' : 'Notifier les absents'}
                  </button>
                )}
              </div>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '16px', marginTop: '4px' }}>
                Ces participants ont signalé leur intention de participer via le portail fidèle. Leur présence physique est confirmée lors du scan QR.
              </p>
              {/* Filtre inscrits */}
              {registrations.length > 4 && (
                <div style={{ position: 'relative', marginBottom: '12px' }}>
                  <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                    placeholder="Filtrer les inscrits..." value={regFilter} onChange={e => setRegFilter(e.target.value)} />
                </div>
              )}
              {registrations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 10px' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Aucun participant inscrit pour le moment.
                </div>
              ) : (() => {
                const STATUS_LABELS_FR = { nouveau: 'Nouveau', membre: 'Membre', serviteur: 'Serviteur', invite: 'Invité' };
                const filtered = registrations.filter(r => !regFilter || r.full_name.toLowerCase().includes(regFilter.toLowerCase()));
                const presents = filtered.filter(r => presentIds.has(r.participant_id));
                const absents  = filtered.filter(r => !presentIds.has(r.participant_id));
                const isPast   = activity.status === 'completed' || activity.status === 'cancelled';

                const RegRow = ({ reg, isPresent }) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', backgroundColor: isPresent ? '#f0fdf4' : isPast ? '#fff7ed' : '#f8fafc', borderRadius: '10px', border: `1px solid ${isPresent ? '#bbf7d0' : isPast ? '#fed7aa' : '#e2e8f0'}` }}>
                    {reg.photo
                      ? <img src={reg.photo} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: isPresent ? '#dcfce7' : isPast ? '#fee2e2' : '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 800, fontSize: '0.85rem', color: isPresent ? '#16a34a' : isPast ? '#b91c1c' : '#4f46e5' }}>
                          {reg.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                    }
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{reg.full_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {reg.member_code} · {STATUS_LABELS_FR[reg.status] || reg.status}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {isPresent ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', backgroundColor: '#dcfce7', borderRadius: '999px', padding: '3px 10px' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          Présent
                        </span>
                      ) : isPast ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', fontWeight: 700, color: '#b91c1c', backgroundColor: '#fee2e2', borderRadius: '999px', padding: '3px 10px' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          Absent
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#f59e0b', backgroundColor: '#fef9c3', borderRadius: '999px', padding: '3px 10px' }}>
                          En attente
                        </span>
                      )}
                      <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '3px' }}>
                        Inscrit le {new Date(reg.registered_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Section Présents */}
                    {presents.length > 0 && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Présents — {presents.length}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {presents.map(r => <RegRow key={r.id} reg={r} isPresent={true} />)}
                        </div>
                      </div>
                    )}
                    {/* Section Absents — toujours visible quand activité terminée */}
                    {(isPast || absents.length > 0) && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isPast
                              ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                 <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Absents — {absents.length}</span></>
                              : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                 <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>En attente — {absents.length}</span></>
                            }
                          </div>
                          {isPast && absents.length > 0 && (
                            <button onClick={downloadAbsentsCSV}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', backgroundColor: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '7px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700, color: '#c2410c' }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                              Télécharger CSV
                            </button>
                          )}
                        </div>
                        {absents.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '16px', color: '#16a34a', fontSize: '0.85rem', fontWeight: 600, backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                            Tous les inscrits étaient présents !
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {absents.map(r => <RegRow key={r.id} reg={r} isPresent={false} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'individual' && (
            <div style={styles.card}>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>
                Rechercher un participant
              </div>
              <input
                style={styles.searchInput}
                placeholder="Nom, prénom ou téléphone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {searchLoading && <LoadingSpinner size={20} />}
              {searchResults.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  {searchResults.map((p) => (
                    <div key={p.id} style={styles.participantRow}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{p.full_name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{p.gender} · {p.status}{p.phone ? ` · ${p.phone}` : ''}</div>
                      </div>
                      <button style={styles.btnPrimary} onClick={() => markPresent(p)}><CheckIcon size={13} color="white" /> Présent</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Liste des présents avec heure de scan */}
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <span>Présents enregistrés ({attendees.filter((a) => !a.is_anonymous).length})</span>
                {attendees.filter(a => !a.is_anonymous && !a.recorded_by).length > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', fontWeight: 600, color: '#16a34a', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '999px', padding: '3px 10px' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/></svg>
                    {attendees.filter(a => !a.is_anonymous && !a.recorded_by).length} via QR scan
                  </span>
                )}
              </div>
              {/* Filtre présents */}
              {attendees.filter(a => !a.is_anonymous).length > 4 && (
                <div style={{ position: 'relative', marginBottom: '10px' }}>
                  <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                    placeholder="Filtrer les présents..." value={attendeeFilter} onChange={e => setAttendeeFilter(e.target.value)} />
                </div>
              )}
              {attendees.filter((a) => !a.is_anonymous).length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Aucun participant enregistré individuellement.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {attendees.filter((a) => !a.is_anonymous)
                    .filter(a => !attendeeFilter || (a.participant_detail?.full_name || '').toLowerCase().includes(attendeeFilter.toLowerCase()))
                    .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))
                    .map((rec) => {
                      const scanDate = rec.recorded_at ? new Date(rec.recorded_at) : null;
                      const isQR = !rec.recorded_by;
                      return (
                        <div key={rec.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', backgroundColor: isQR ? '#f0fdf4' : '#f8fafc', borderRadius: '9px', border: `1px solid ${isQR ? '#bbf7d0' : '#e2e8f0'}` }}>
                          {/* Avatar initiales */}
                          <div style={{ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: isQR ? '#16a34a22' : '#1e40af18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 800, fontSize: '0.85rem', color: isQR ? '#16a34a' : '#1e40af' }}>
                            {(rec.participant_detail?.full_name || 'IN').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{rec.participant_detail?.full_name || 'Inconnu'}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{rec.participant_detail?.status}</span>
                              {isQR ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.65rem', fontWeight: 700, color: '#16a34a', backgroundColor: '#dcfce7', borderRadius: '999px', padding: '1px 6px' }}>
                                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><rect x="7" y="7" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/></svg>
                                  QR scan autonome
                                </span>
                              ) : (
                                <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>par {rec.recorded_by_name || 'admin'}</span>
                              )}
                            </div>
                          </div>
                          {/* Heure du scan */}
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {scanDate && (
                              <>
                                <div style={{ fontSize: '0.85rem', fontWeight: 800, color: isQR ? '#16a34a' : '#1e40af' }}>
                                  {scanDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                  {scanDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                                </div>
                              </>
                            )}
                            {canEdit && (
                              <button
                                style={{ marginTop: '4px', background: 'none', color: '#b91c1c', border: 'none', cursor: 'pointer', fontSize: '0.7rem', padding: 0, textDecoration: 'underline' }}
                                onClick={() => setConfirmRemove(rec.id)}
                              >
                                Retirer
                              </button>
                            )}
                          </div>
                        </div>
                      );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Read-only summary for consultation users */}
      {!canEdit && (
        <div style={styles.card}>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>
            Résumé des présences
          </div>
          {bulkCount ? (
            <div style={styles.countGrid}>
              {COUNT_FIELDS.map((f) => (
                <div key={f.key} style={styles.countCell}>
                  <div style={styles.countLabel}>{f.label}</div>
                  <div style={styles.countValue}>{bulkCount[f.key]}</div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#94a3b8' }}>Aucune donnée de comptage disponible.</p>
          )}
        </div>
      )}
    </div>
  );
}
